import Card from '$tablet-app/src/app/components/Card';
import { DropdownMenu } from '$tablet-app/src/app/components/DropdownMenu/DropdownMenu';
import { DropDownMenuOption } from '$tablet-app/src/app/components/DropdownMenu/DropdownMenu.props';
import EditBadge from '$tablet-app/src/app/components/EditBadge';
import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import { InfoDialogButton } from '$tablet-app/src/app/components/InfoDialogButton';
import { Panel } from '$tablet-app/src/app/components/Panel';
import Row from '$tablet-app/src/app/components/Row';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenDeleteButton } from '$tablet-app/src/app/components/ScreenDeleteButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenSaveButton } from '$tablet-app/src/app/components/ScreenSaveButton';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { setImageFieldsFromInput } from '$tablet-app/src/app/images/actions';
import ImagePicker from '$tablet-app/src/app/images/components/ImagePicker';
import useRecordThumbnail from '$tablet-app/src/app/images/hooks/useRecordThumbnail';
import { LocalImage } from '$tablet-app/src/app/images/types';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { updateItemInListByIndex } from '$tablet-app/src/app/utils/arrays';
import useBool from '$tablet-app/src/app/utils/state/useBool';
import { Address } from '@akin-home/amplify';
import { append, removeByIndex } from '@akin-home/arrays';
import { Button } from '@akin-home/buttons/native';
import {
  ContactModel,
  ContactTypes,
  PhoneNumber,
  PhoneTypes,
  addAddressToContact,
  contact_tags,
} from '@akin-home/contacts';
import { LargeInput } from '@akin-home/forms/native';
import { colors } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { Switch } from 'react-native-switch';
import styled from 'styled-components/native';
import { useSetContactFavourite } from '../../hooks/useSetContactFavourite';
import { AddContactDetailModal } from '../AddContactDetailModal';
import ContactTagButton from '../ContactTagButton';
import { AddressField } from './AddressField';
import { KeyboardAvoid } from '$tablet-app/src/app/components/KeyboardAvoid';

interface Props {
  submitting?: boolean;
  contact: ContactModel;
  types?: ContactTypes;
  is_member?: boolean;
  setIsMember(is_member: boolean): void;

  onCancel?: () => void;
  onSubmit?: () => void;
  onChange?: (contact: ContactModel) => void;
  onDelete?: () => void;
}

const ContactFormScreenView = (props: Props) => {
  const {
    contact,
    is_member,
    setIsMember,
    onCancel,
    onSubmit,
    onChange,
    onDelete,
  } = props;

  const [detail_dialog_visible, showDetailDialog, hideDetailDialog] = useBool();

  const favourite_contact_ids = useStateStore(
    (state) => state.current_user?.favouriteContacts
  );

  const is_favourite = favourite_contact_ids?.includes(contact.id);

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();

  const toggleFavourite = useSetContactFavourite();

  const toggleIsFavourite = () => toggleFavourite(contact);

  const onChangeName = (name: string) => onChange?.({ ...contact, name });

  const onAddDetail = (type: string) => {
    hideDetailDialog();

    if (!onChange) return;

    switch (type) {
      case 'phone':
        onChange(contact.addPhoneNumber());
        break;
      case 'email':
        onChange({
          ...contact,
          emailAddresses: append(contact.emailAddresses, ''),
        });
        break;
      case 'note':
        onChange({
          ...contact,
          notes: append(contact.notes, ''),
        });
        break;
      case 'address':
        onChange(addAddressToContact(contact));
        break;
    }
  };

  const onChangeEmail = (idx: number) => (email: string) =>
    onChange?.({
      ...contact,
      emailAddresses: updateItemInListByIndex(
        contact.emailAddresses,
        idx,
        email
      ),
    });

  const onDeleteEmail = (idx: number) => () =>
    onChange?.({
      ...contact,
      emailAddresses: removeByIndex(contact.emailAddresses, idx),
    });

  const onChangeNote = (idx: number) => (note: string) =>
    onChange?.({
      ...contact,
      notes: updateItemInListByIndex(contact.notes, idx, note),
    });

  const onDeleteNote = (idx: number) => () =>
    onChange?.({
      ...contact,
      notes: removeByIndex(contact.notes, idx),
    });

  const onChangeAddress = (idx: number) => (address: Address) =>
    onChange?.({
      ...contact,
      addresses: updateItemInListByIndex(contact.addresses || [], idx, address),
    });

  const onDeleteAddress = (idx: number) => () =>
    onChange?.({
      ...contact,
      addresses: removeByIndex(contact.addresses || [], idx),
    });

  const onRemoveNumber = (index: number) =>
    onChange?.(contact.removePhoneNumber(index));

  const onUpdatePhoneNumber = (index: number, phoneNumber: PhoneNumber) =>
    onChange?.(contact.updatePhoneNumber(index, phoneNumber));

  const onChangeImageField = (imageData: {
    url?: string;
    imageUpload?: LocalImage;
  }) => onChange?.(setImageFieldsFromInput(contact, imageData));

  const onToggleTag = (label: ContactTypes) =>
    onChange?.({
      ...contact,
      tags: contact.tags.includes(label)
        ? contact.tags.filter((t) => t !== label)
        : [...contact.tags, label],
    });

  const { uri } = useRecordThumbnail({ record: contact });

  return (
    <Screen>
      <Header>
        <ScreenBackButton onPress={onCancel} />

        <ScreenTitle>
          {contact.id === '' ? 'New contact' : 'Change contact'}
        </ScreenTitle>
      </Header>
      
      <KeyboardAvoid>
        <Scroll
          contentContainerStyle={styles.scroll}
          onScroll={scrollHandler}
        >
          <TopCard>
            <ImageContainer
              imageUrl={contact.imageUrl}
              onChange={onChangeImageField}
            >
              {uri ? (
                <ContactImage source={{ uri }} />
              ) : (
                <ImagePlaceholder>
                  <FontAwesomeIcon size={50} color="lightgrey" icon="user" />
                </ImagePlaceholder>
              )}

              <EditBadgeWrapper />
            </ImageContainer>

            <TopDetailsRight>
              <View>
                <LargeInput
                  value={contact.name}
                  placeholder="Name"
                  onChangeText={onChangeName}
                  textContentType="name"
                />
              </View>

              <SwitchField>
                <View>
                  <Switch
                    value={is_member}
                    switchLeftPx={3}
                    switchRightPx={3}
                    barHeight={32}
                    circleSize={25}
                    onValueChange={setIsMember}
                    renderActiveText={false}
                    renderInActiveText={false}
                    backgroundActive={colors.primary[70]}
                    circleBorderWidth={0}
                  />
                </View>
                <Text variant="h3" weight="semibold">
                  Pixi member
                </Text>

                <InfoDialogButton
                  title="Pixi Member"
                  body={
                    <Text weight="semibold" style={{ textAlign: 'center' }}>
                      Is a person you will share your events, meal plan, lists and
                      goals with.
                    </Text>
                  }
                />
              </SwitchField>
            </TopDetailsRight>

            <View>
              <Button
                size="xlarge"
                iconSize={30}
                variant="secondary"
                iconColor={is_favourite ? 'red' : undefined}
                icon={is_favourite ? 'heart' : ['far', 'heart']}
                onPress={toggleIsFavourite}
              />
            </View>
          </TopCard>

          <DetailPanel>
            <DetailLeft>
              <Text variant="h3" weight="semibold">
                Categories
              </Text>
            </DetailLeft>
            <Spacer height="30px" width="1px;" color="lightgrey" />
            <ScrollView horizontal>
              <Row gap={10}>
                {contact_tags.map((tag) => (
                  <ContactTagButton
                    key={tag.label}
                    label={tag.label}
                    emoji={tag.emoji}
                    selected={contact.tags?.includes(tag.label)}
                    onPress={onToggleTag}
                  />
                ))}
              </Row>
            </ScrollView>
          </DetailPanel>

          {contact.emailAddresses.map((email, idx) => (
            <DetailPanel key={idx}>
              <DetailLeft>
                <Text variant="h3" weight="semibold">
                  Email
                </Text>
              </DetailLeft>

              <Spacer height="30px" width="1px;" color="lightgrey" />

              <DetailInput
                value={email}
                onChangeText={onChangeEmail(idx)}
                placeholder="Type email here"
                keyboardType="email-address"
                textContentType="emailAddress"
              />

              <Button
                variant="secondary"
                size="small"
                icon="times"
                onPress={onDeleteEmail(idx)}
              />
            </DetailPanel>
          ))}

          {contact.phoneNumbers.map((phoneNumber, idx) => (
            <ContactFormPhoneInput
              key={idx}
              idx={idx}
              phoneNumber={phoneNumber}
              onChange={onUpdatePhoneNumber}
              onRemove={onRemoveNumber}
            />
          ))}

          {contact.addresses?.map((address, idx) => (
            <DetailPanel key={idx}>
              <DetailLeft>
                <Text variant="h3" weight="semibold">
                  Address
                </Text>
              </DetailLeft>

              <Spacer width="1px;" color="lightgrey" />

              <AddressField address={address} onChange={onChangeAddress(idx)} />

              <View>
                <Button
                  variant="secondary"
                  icon="times"
                  onPress={onDeleteAddress(idx)}
                />
              </View>
            </DetailPanel>
          ))}

          {contact.notes.map((note, idx) => (
            <DetailPanel key={idx}>
              <DetailLeft>
                <Text variant="h3" weight="semibold">
                  Note
                </Text>
              </DetailLeft>

              <Spacer height="30px" width="1px;" color="lightgrey" />

              <DetailInput
                value={note}
                onChangeText={onChangeNote(idx)}
                placeholder="Type note here"
              />

              <Button
                variant="secondary"
                size="small"
                icon="times"
                onPress={onDeleteNote(idx)}
              />
            </DetailPanel>
          ))}

          <DetailPanel>
            <AddNumberCard onPress={showDetailDialog}>
              <Text>
                <FontAwesomeIcon icon="plus" />
              </Text>

              <Text variant="h3" weight="semibold">
                Add new details
              </Text>
            </AddNumberCard>
          </DetailPanel>
        </Scroll>
      </KeyboardAvoid>

      <FloatingActions style={floating_action_styles}>
        {onDelete ? <ScreenDeleteButton onPress={onDelete} /> : null}
        <ScreenSaveButton onPress={onSubmit} />
      </FloatingActions>

      <AddContactDetailModal
        visible={detail_dialog_visible}
        onClose={hideDetailDialog}
        onSubmit={onAddDetail}
      />
    </Screen>
  );
};

interface ContactItemProps {
  idx: number;
  phoneNumber: PhoneNumber;

  onChange: (idx: number, phoneNumber: PhoneNumber) => void;
  onRemove: (idx: number) => void;
}

const options = [
  { id: 'Work', title: 'Work' },
  { id: 'Home', title: 'Home' },
  { id: 'Mobile', title: 'Mobile' },
  { id: 'Personal', title: 'Personal' },
  { id: 'Support', title: 'Support' },
  { id: 'Emergency', title: 'Emergency' },
  { id: 'Custom', title: 'Custom' },
];

const ContactFormPhoneInput = (props: ContactItemProps) => {
  const { idx, phoneNumber, onChange, onRemove } = props;

  const onChangePhoneNumber = (number: string) =>
    onChange(idx, { ...phoneNumber, number });

  const onRemovePhoneNumber = () => onRemove(idx);

  const onChangeType = (opt: DropDownMenuOption) =>
    onChange(idx, { ...phoneNumber, type: opt.id as PhoneTypes });

  return (
    <DetailPanel>
      <DetailLeft>
        <DropdownMenu
          options={options}
          onChange={onChangeType}
          value={options.find((option) => option.id === phoneNumber.type)}
          anchor={({ showPopUp }) => (
            <PhoneNumberTypeContainer onPress={showPopUp}>
              <Text variant="h3" weight="semibold">
                {phoneNumber.type || 'Select Type'}
              </Text>

              <FontAwesomeIcon icon="chevron-down" />
            </PhoneNumberTypeContainer>
          )}
        />
      </DetailLeft>

      <Spacer height="30px" width="1px;" color="lightgrey" />

      <DetailInput
        placeholder="Phone"
        value={phoneNumber.number}
        onChangeText={onChangePhoneNumber}
        keyboardType="phone-pad"
        textContentType="telephoneNumber"
      />

      <Button
        variant="secondary"
        size="small"
        icon="times"
        onPress={onRemovePhoneNumber}
      />
    </DetailPanel>
  );
};

const PhoneNumberTypeContainer = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  gap: 34px;
`;

const DetailInput = styled.TextInput`
  flex: 1;
  font-size: 22px;
  font-weight: 600;
`;

const Screen = styled.View`
  flex: 1;
`;

const TopCard = styled(Card)`
  flex-direction: row;
  padding: 16px;
  gap: 34px;
`;

const AddNumberCard = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const ImagePlaceholder = styled.View`
  height: 131px;
  width: 131px;

  border-radius: 50px;

  justify-content: center;
  align-items: center;

  background-color: #f5f7f9;
`;

const ContactImage = styled.Image`
  height: 144px;
  width: 144px;
  border-radius: 50px;
`;

const ImageContainer = styled(ImagePicker)`
  position: relative;
`;

const EditBadgeWrapper = styled(EditBadge)`
  position: absolute;
  top: -10px;
  right: -10px;
  width: 53px;
  height: 53px;
`;
const TopDetailsRight = styled.View`
  flex: 1;
  gap: 20px;
`;

const Header = styled(ScreenHeader)`
  gap: 20px;
  justify-content: flex-start;
  align-items: center;
`;

const DetailPanel = styled(Panel)`
  padding: 18px 24px;
  flex-direction: row;
  gap: 35px;
`;

const DetailLeft = styled.View`
  width: 160px;
`;

const SwitchField = styled.View`
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const Scroll = styled(Animated.ScrollView)`
  flex: 1;
`

const styles = StyleSheet.create({
  scroll: {
    gap: 16,
    paddingHorizontal: 20,
    paddingBottom: 100,
  },
});

export default ContactFormScreenView;
